import { comparisonToExpression, OPERATOR_LABELS, type Comparison, type ScopeVariable } from './condition-model';

/**
 * Conditions à groupes imbriqués (ADR-146 addendum (3)) : « Toutes ces conditions », « Au moins une de ces
 * conditions », « Aucune de ces conditions », trois niveaux au plus. Une condition se construit par listes
 * et se relit en phrase ; elle se traduit en expression canonique d'ADR-039 (`$and`, `$or`, `$not`) évaluée
 * par l'évaluateur unique, jamais `eval`. Toute expression valide jusqu'à trois niveaux est relue en
 * groupes ; au-delà, ou avec des calculs, elle est « hors de portée » : gardée telle quelle, jamais montrée
 * en brut (population non informaticienne, addendum (2)). Générique : la portée vient du métier.
 */

export type GroupMode = 'all' | 'any' | 'none';

export interface ConditionLeaf extends Comparison {
  kind: 'comparison';
}

export interface ConditionGroup {
  kind: 'group';
  mode: GroupMode;
  items: ConditionNode[];
}

export type ConditionNode = ConditionLeaf | ConditionGroup;

/** Profondeur maximale des groupes : le groupe de départ est le niveau 1. */
export const MAX_GROUP_DEPTH = 3;

export const GROUP_LABELS: Readonly<Record<GroupMode, string>> = {
  all: 'Toutes ces conditions',
  any: 'Au moins une de ces conditions',
  none: 'Aucune de ces conditions',
};

const isRecord = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === 'object' && !Array.isArray(value);
const isRef = (value: unknown): value is string => typeof value === 'string' && /^\$[a-z][a-z0-9_]*(\.[a-z0-9_]+)*$/.test(value);

export const emptyGroup = (): ConditionGroup => ({ kind: 'group', mode: 'all', items: [] });

function unliteral(value: unknown): { ok: boolean; value?: unknown } {
  if (isRecord(value) && Object.keys(value).length === 1 && '$literal' in value) return { ok: true, value: value['$literal'] };
  if (value === null || typeof value === 'number' || typeof value === 'boolean') return { ok: true, value };
  if (typeof value === 'string') return value.startsWith('$') ? { ok: false } : { ok: true, value };
  if (Array.isArray(value)) {
    const items = value.map(unliteral);
    return items.every((item) => item.ok) ? { ok: true, value: items.map((item) => item.value) } : { ok: false };
  }
  return { ok: false };
}

/** Une comparaison lue dans une expression (`$eq`, `$isEmpty`, « est renseigné »…), sinon `null`. */
export function parseLeaf(expression: unknown): ConditionLeaf | null {
  if (!isRecord(expression) || Object.keys(expression).length !== 1) return null;
  const [operator, args] = Object.entries(expression)[0]!;
  if (!Array.isArray(args)) return null;
  if (operator === '$isEmpty' && args.length === 1 && isRef(args[0])) return { kind: 'comparison', ref: args[0], op: 'empty' };
  if (operator === '$not' && args.length === 1) {
    const inner = parseLeaf(args[0]);
    return inner !== null && inner.op === 'empty' ? { kind: 'comparison', ref: inner.ref, op: 'filled' } : null;
  }
  const op = operator.slice(1) as Comparison['op'];
  if (!['eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'in', 'nin'].includes(op) || args.length !== 2 || !isRef(args[0])) return null;
  const value = unliteral(args[1]);
  if (!value.ok) return null;
  if ((op === 'in' || op === 'nin') && !Array.isArray(value.value)) return null;
  return { kind: 'comparison', ref: args[0], op, value: value.value };
}

function parseNode(expression: unknown, depth: number): ConditionNode | null {
  const leaf = parseLeaf(expression);
  if (leaf !== null) return leaf;
  return parseGroup(expression, depth);
}

function parseGroup(expression: unknown, depth: number): ConditionGroup | null {
  if (depth > MAX_GROUP_DEPTH || !isRecord(expression) || Object.keys(expression).length !== 1) return null;
  const [operator, args] = Object.entries(expression)[0]!;
  if (!Array.isArray(args)) return null;
  let mode: GroupMode;
  let members: unknown[];
  if (operator === '$and' || operator === '$or') {
    mode = operator === '$and' ? 'all' : 'any';
    members = args;
  } else if (operator === '$not' && args.length === 1) {
    const inner = args[0];
    mode = 'none';
    // « Aucune » : la négation d'un « au moins une » ; une négation seule vaut « aucune » d'un élément.
    if (isRecord(inner) && Object.keys(inner).length === 1 && Array.isArray(inner['$or'])) members = inner['$or'] as unknown[];
    else if (parseLeaf(inner) !== null) members = [inner];
    else return null;
  } else return null;
  const items: ConditionNode[] = [];
  for (const member of members) {
    const node = parseNode(member, depth + 1);
    if (node === null) return null;
    items.push(node);
  }
  return { kind: 'group', mode, items };
}

/**
 * Relit une expression en groupe de conditions ; `null` si elle sort de portée (calcul, fonction, plus de
 * trois niveaux). Absente : un groupe vide (toujours vrai).
 */
export function fromExpression(expression: unknown): ConditionGroup | null {
  if (expression === undefined || expression === null) return emptyGroup();
  const leaf = parseLeaf(expression);
  if (leaf !== null) return { kind: 'group', mode: 'all', items: [leaf] };
  return parseGroup(expression, 1);
}

/** Expression d'un nœud ; un groupe vide vaut `undefined` (sans effet). */
export function nodeExpression(node: ConditionNode): unknown {
  if (node.kind === 'comparison') return node.ref === '' ? undefined : comparisonToExpression(node);
  const parts = node.items.map(nodeExpression).filter((part) => part !== undefined);
  if (parts.length === 0) return undefined;
  if (node.mode === 'all') return { $and: parts };
  if (node.mode === 'any') return { $or: parts };
  return { $not: [{ $or: parts }] };
}

/**
 * Expression canonique d'une condition ; le groupe de départ « toutes » d'une seule comparaison s'écrit
 * comme la comparaison seule (forme des gabarits écrits à la main). Vide : `undefined` (la clé disparaît).
 */
export function toExpression(root: ConditionGroup): unknown {
  const parts = root.items.map(nodeExpression).filter((part) => part !== undefined);
  if (parts.length === 0) return undefined;
  if (root.mode === 'all' && parts.length === 1 && root.items[0]!.kind === 'comparison') return parts[0];
  return nodeExpression(root);
}

/** Profondeur d'un groupe (1 pour un groupe sans sous-groupe). */
export function groupDepth(group: ConditionGroup): number {
  return 1 + Math.max(0, ...group.items.filter((item): item is ConditionGroup => item.kind === 'group').map(groupDepth));
}

// ---- Édition par chemin d'indices (`[]` : groupe de départ ; `[1, 0]` : premier élément du deuxième).

export type NodePath = readonly number[];

export function nodeAt(root: ConditionGroup, path: NodePath): ConditionNode | undefined {
  let node: ConditionNode | undefined = root;
  for (const index of path) node = node?.kind === 'group' ? node.items[index] : undefined;
  return node;
}

/** Remplace (ou retire, avec `undefined`) le nœud au chemin ; rend un nouvel arbre. */
export function replaceAt(root: ConditionGroup, path: NodePath, next: ConditionNode | undefined): ConditionGroup {
  if (path.length === 0) return next !== undefined && next.kind === 'group' ? next : root;
  const [head, ...rest] = path;
  const items = [...root.items];
  if (rest.length === 0) {
    if (next === undefined) items.splice(head!, 1);
    else items[head!] = next;
  } else {
    const child = items[head!];
    if (child?.kind !== 'group') return root;
    items[head!] = replaceAt(child, rest, next);
  }
  return { ...root, items };
}

/** Ajoute un nœud à la fin du groupe au chemin ; un sous-groupe au-delà de trois niveaux est refusé. */
export function appendAt(root: ConditionGroup, path: NodePath, node: ConditionNode): ConditionGroup {
  const group = nodeAt(root, path);
  if (group?.kind !== 'group') return root;
  // Le groupe au chemin est au niveau `path.length + 1` ; le sous-groupe ajouté ne dépasse pas le troisième.
  if (node.kind === 'group' && path.length + 1 + groupDepth(node) > MAX_GROUP_DEPTH) return root;
  return replaceAt(root, path, { ...group, items: [...group.items, node] });
}

/** Copie d'un nœud placée juste après lui. */
export function duplicateAt(root: ConditionGroup, path: NodePath): ConditionGroup {
  if (path.length === 0) return root;
  const parentPath = path.slice(0, -1);
  const parent = nodeAt(root, parentPath);
  const node = nodeAt(root, path);
  if (parent?.kind !== 'group' || node === undefined) return root;
  const items = [...parent.items];
  items.splice(path[path.length - 1]! + 1, 0, JSON.parse(JSON.stringify(node)) as ConditionNode);
  return replaceAt(root, parentPath, { ...parent, items });
}

// ---- Lecture en phrase.

export interface SentencePart {
  text: string;
  /** Niveau du groupe (0 : départ) pour la couleur ; `join` : un mot de liaison (ET, OU, NI, PAS). */
  depth: number;
  join?: boolean;
}

function valueText(leaf: ConditionLeaf, variable: ScopeVariable | undefined): string {
  const one = (value: unknown): string => variable?.options?.find((option) => option.value === String(value))?.label ?? (typeof value === 'boolean' ? (value ? 'oui' : 'non') : value === undefined || value === null ? '…' : String(value));
  return Array.isArray(leaf.value) ? leaf.value.map(one).join(', ') : one(leaf.value);
}

/** Une comparaison en mots : « Espèce est égal à Sanglier ». */
export function leafText(leaf: ConditionLeaf, variables: readonly ScopeVariable[]): string {
  const variable = variables.find((candidate) => candidate.ref === leaf.ref);
  const label = variable?.label ?? 'une valeur inconnue';
  return leaf.op === 'filled' || leaf.op === 'empty' ? `${label} ${OPERATOR_LABELS[leaf.op]}` : `${label} ${OPERATOR_LABELS[leaf.op]} ${valueText(leaf, variable)}`;
}

/** Morceaux de la phrase, pour un rendu coloré par niveau. */
export function sentenceParts(root: ConditionGroup, variables: readonly ScopeVariable[]): SentencePart[] {
  const walk = (group: ConditionGroup, depth: number): SentencePart[] => {
    const out: SentencePart[] = [];
    if (group.mode === 'none') out.push({ text: 'aucune de :', depth, join: true });
    group.items.forEach((item, index) => {
      if (index > 0) out.push({ text: group.mode === 'all' ? 'ET' : group.mode === 'any' ? 'OU' : 'NI', depth, join: true });
      if (item.kind === 'comparison') out.push({ text: leafText(item, variables), depth });
      else {
        out.push({ text: '(', depth: depth + 1, join: true });
        out.push(...walk(item, depth + 1));
        out.push({ text: ')', depth: depth + 1, join: true });
      }
    });
    return out;
  };
  return walk(root, 0);
}

/** La phrase entière, en texte simple (résumé d'un bloc, lecteur d'écran). */
export function sentence(root: ConditionGroup, variables: readonly ScopeVariable[]): string {
  return sentenceParts(root, variables)
    .map((part) => part.text)
    .join(' ')
    .replace(/\( /g, '(')
    .replace(/ \)/g, ')');
}
